import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import projectService, { Package } from '../../services/projectService';
import api from '../../services/api';
import { useNotification } from '../layouts/Layout';
import { searchGoogleContacts, GoogleContactSuggestion } from '../../services/contactsService';

interface MobileInquiryFormProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  initialPackage?: Package | null;
  packages?: Package[];
}

const MobileInquiryForm: React.FC<MobileInquiryFormProps> = ({ open, onClose, projectId, projectName, initialPackage = null, packages }) => {
  const { showNotification } = useNotification();
  const [availablePackages, setAvailablePackages] = useState<Package[]>(packages || []);
  const [selectedPackageId, setSelectedPackageId] = useState<string>(initialPackage?.package_id || '');
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(initialPackage || null);
  const [contractorEmail, setContractorEmail] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactSuggestions, setContactSuggestions] = useState<GoogleContactSuggestion[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [created, setCreated] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  useEffect(() => {
    if (!packages && projectId) {
      (async () => {
        try {
          const pkgs = await projectService.getPackagesByProjectId(projectId);
          setAvailablePackages(pkgs);
        } catch {
          setAvailablePackages([]);
        }
      })();
    }
  }, [packages, projectId]);

  // Ustaw wybrany pakiet, gdy przychodzi z propsów (np. z listy pakietów)
  useEffect(() => {
    if (initialPackage && initialPackage.package_id) {
      setSelectedPackageId(initialPackage.package_id);
      setSelectedPackage(initialPackage);
      // Upewnij się, że opcja z tym pakietem istnieje w selekcie
      setAvailablePackages(prev => {
        if (!prev || prev.length === 0) return [initialPackage];
        const exists = prev.some(p => p.package_id === initialPackage.package_id);
        return exists ? prev : [initialPackage, ...prev];
      });
    }
  }, [initialPackage]);

  // Gdy okno się otwiera – jeszcze raz ustaw domyślny pakiet
  useEffect(() => {
    if (open && initialPackage?.package_id) {
      setSelectedPackageId(initialPackage.package_id);
      setSelectedPackage(initialPackage);
    }
  }, [open, initialPackage]);

  useEffect(() => {
    const pkg = availablePackages.find(p => p.package_id === selectedPackageId) || null;
    setSelectedPackage(pkg || null);
  }, [selectedPackageId, availablePackages]);

  useEffect(() => {
    // Domyślny temat inspirowany desktopem
    const pkgName = selectedPackage?.name || initialPackage?.name || '';
    const base = `${projectName || ''}: Zapytanie ofertowe${pkgName ? ' - ' + pkgName : ''}`;
    setSubject(base);
  }, [projectName, selectedPackage, initialPackage]);

  useEffect(() => {
    if (contactQuery.trim().length < 2) {
      setContactSuggestions([]);
      return;
    }
    let active = true;
    setContactsLoading(true);
    searchGoogleContacts(contactQuery).then(list => { if (active) setContactSuggestions(list); }).finally(() => setContactsLoading(false));
    return () => { active = false; };
  }, [contactQuery]);

  const handleSubmit = async () => {
    try {
      if (!selectedPackage?.package_id) {
        showNotification('Wybierz pakiet', 'error');
        return;
      }
      if (!contractorEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contractorEmail)) {
        showNotification('Podaj poprawny adres e‑mail', 'error');
        return;
      }
      setLoading(true);
      const createdInquiry = await projectService.createInquiry({
        project_id: projectId,
        package_id: selectedPackage.package_id,
        contractor_email: contractorEmail,
        subject
      });
      setCreatedId(createdInquiry?.inquiry_id || null);
      setCreated(true);
      showNotification(`Zapytanie utworzone${createdInquiry?.inquiry_id ? ` (#${createdInquiry.inquiry_id})` : ''}`, 'success');
      setTimeout(() => { setCreated(false); onClose(); }, 900);
    } catch (e: any) {
      showNotification(e?.message || 'Nie udało się utworzyć zapytania', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createIfNeeded = async (): Promise<string | null> => {
    if (!selectedPackage?.package_id) {
      showNotification('Wybierz pakiet', 'error');
      return null;
    }
    if (!contractorEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contractorEmail)) {
      showNotification('Podaj poprawny adres e‑mail', 'error');
      return null;
    }
    const createdInquiry = await projectService.createInquiry({
      project_id: projectId,
      package_id: selectedPackage.package_id,
      contractor_email: contractorEmail,
      subject
    });
    return createdInquiry?.inquiry_id || null;
  };

  const handleCreateDraft = async () => {
    try {
      setLoading(true);
      const id = await createIfNeeded();
      if (!id) return;
      const res = await api.post(`/inquiries/${id}/create-gmail-draft`);
      if (res.data?.success) {
        showNotification('Utworzono draft w Gmail', 'success');
        setCreatedId(id);
        setCreated(true);
        setTimeout(() => { setCreated(false); onClose(); }, 900);
      } else {
        showNotification(res.data?.message || 'Nie udało się utworzyć draftu', 'error');
      }
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Błąd tworzenia draftu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendDirect = async () => {
    try {
      setLoading(true);
      const id = await createIfNeeded();
      if (!id) return;
      const res = await api.post(`/inquiries/${id}/send-direct-email`);
      if (res.data?.success) {
        showNotification('Wiadomość wysłana', 'success');
        setCreatedId(id);
        setCreated(true);
        setTimeout(() => { setCreated(false); onClose(); }, 900);
      } else {
        showNotification(res.data?.message || 'Nie udało się wysłać wiadomości', 'error');
      }
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Błąd wysyłania', 'error');
    } finally {
      setLoading(false);
    }
  };

  const moneyK = useMemo(() => (v?: number) => {
    if (v === undefined || v === null) return '—';
    const k = Math.round(Number(v) / 1000);
    try { return new Intl.NumberFormat('pl-PL').format(k) + ' k'; } catch { return k + ' k'; }
  }, []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }} PaperProps={{ sx: { borderRadius: 3, mt: 'calc(88px + env(safe-area-inset-top))' } }}>
      <DialogTitle sx={{ display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', columnGap: 1, pr:1 }}>
        <IconButton onClick={onClose} aria-label="zamknij" sx={{ justifySelf:'start', width: 40, height: 40 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography component="span" variant="h6" sx={{ textAlign:'center', fontWeight:700 }}>Nowe zapytanie</Typography>
        <Box sx={{ width: 40, height: 40 }} />
      </DialogTitle>
      <DialogContent dividers>
        {created ? (
          <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py: 6 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Utworzono</Typography>
            {createdId && (<Typography variant="body2" color="text.secondary">#{createdId}</Typography>)}
          </Box>
        ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: loading ? 0.7 : 1 }}>
          <Typography variant="subtitle2" color="text.secondary">Pakiet</Typography>
          <TextField
            select
            SelectProps={{ native: true }}
            value={selectedPackageId}
            onChange={(e) => setSelectedPackageId(e.target.value)}
            size="small"
            disabled={loading}
          >
            <option value="" disabled>Wybierz pakiet…</option>
            {availablePackages.map(p => (
              <option key={p.package_id} value={p.package_id}>{p.package_id} — {p.name} ({moneyK(p.estimated_value)})</option>
            ))}
          </TextField>

          <Typography variant="subtitle2" color="text.secondary">Kontrahent</Typography>
          <TextField
            placeholder="Wyszukaj kontakt lub wpisz e‑mail"
            size="small"
            value={contactQuery}
            onChange={(e)=> setContactQuery(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            disabled={loading}
          />
          {contactSuggestions.length > 0 && (
            <PaperLike>
              <List dense>
                {contactSuggestions.slice(0, 5).map((c, idx) => (
                  <ListItem key={idx} button onClick={()=>{ setContractorEmail(c.email); setContactQuery(`${c.name} <${c.email}>`); }}>
                    <ListItemText primary={c.name} secondary={c.email} />
                  </ListItem>
                ))}
              </List>
            </PaperLike>
          )}

          <Typography variant="subtitle2" color="text.secondary">Adres e‑mail</Typography>
          <TextField
            type="email"
            size="small"
            value={contractorEmail}
            onChange={(e)=> setContractorEmail(e.target.value)}
            placeholder="adres@firma.pl"
            disabled={loading}
          />

          <Typography variant="subtitle2" color="text.secondary">Temat</Typography>
          <TextField
            size="small"
            value={subject}
            onChange={(e)=> setSubject(e.target.value)}
            disabled={loading}
          />

          <Button variant="contained" onClick={handleSubmit} disabled={loading || !selectedPackageId || !contractorEmail} sx={{ mt: 1 }}>
            Utwórz zapytanie
          </Button>
          <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 1, mt: 1 }}>
            <Button variant="outlined" onClick={handleCreateDraft} disabled={loading || !selectedPackageId || !contractorEmail}>Draft (Gmail)</Button>
            <Button variant="outlined" onClick={handleSendDirect} disabled={loading || !selectedPackageId || !contractorEmail}>Wyślij (Gmail)</Button>
          </Box>
        </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Prosty kontener wyglądający jak karta (użyty dla listy sugestii)
const PaperLike: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>{children}</Box>
);

export default MobileInquiryForm;


